import { ElementRef, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  ViewChild,
  ViewContainerRef,
  computed,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';

import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { OverlayRef, Overlay, OverlayModule } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import {
  Workspace,
  WorkspaceService
} from '@core/services/auth/workspace.service';
import { WorkspaceStore } from '../../../../shared/services/workspace.store';
import { UIFeedbackService } from '../../../../shared/services/ui-feedback.service';
import { AuthStore } from '@core/services/auth/auth.store';
import type { WorkspaceSwitchMarker } from '@core/services/infra/authenticated-loading-coordinator.service';
import {
  getWorkspaceColor as resolveWorkspaceColor,
  getWorkspaceInitials as resolveWorkspaceInitials,
  buildWorkspaceSwitchRail,
  type WorkspaceSwitchRailEntry
} from '@shared/utils/workspace-identity.util';

export type WorkspaceSortKey = 'name' | 'properties' | 'lastVisit';
export type SortDirection = 'asc' | 'desc';

interface DisplayWorkspace {
  raw: Workspace;
  id: string;
  name: string;
  initials: string;
  color: string;
  role: string;
  typeLabel: string;
  propertyCount: number;
  unitsCount: number | null;
  lastActivity: number;
  warningCount: number;
  monthlyRentEUR: number | null;
  cashOverdueEUR: number | null;
  cashIsOnTrack: boolean | null;
  inboxUrgentCount: number;
  inboxTotalCount: number;
  isShared: boolean;
}

const TYPE_LABEL_KEYS: Record<string, string> = {
  personal: 'shared.workspace.types.personal',
  professional: 'shared.workspace.types.professional',
  household: 'shared.workspace.types.household',
  business: 'shared.workspace.types.business',
  agency: 'shared.workspace.types.agency',
  service_provider: 'shared.workspace.types.serviceProvider',
  tenant: 'shared.workspace.types.tenant',
  syndic: 'shared.workspace.types.syndic'
};

const ROLE_LABEL_KEYS: Record<string, string> = {
  owner: 'shared.workspace.roles.owner',
  admin: 'shared.workspace.roles.admin',
  manager: 'shared.workspace.roles.manager',
  standard: 'shared.workspace.roles.member',
  viewer: 'shared.workspace.roles.viewer'
};

@Component({
  selector: 'vex-toolbar-workspace-switcher',
  templateUrl: './toolbar-workspace-switcher.component.html',
  styleUrls: ['./toolbar-workspace-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
    OverlayModule,
    TranslatePipe
  ]
})
export class ToolbarWorkspaceSwitcherComponent {
  private readonly workspaceService = inject(WorkspaceService);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly uiFeedback = inject(UIFeedbackService);
  private readonly authStore = inject(AuthStore);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly overlay = inject(Overlay);
  private readonly vcr = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('paletteTpl') paletteTpl?: TemplateRef<unknown>;
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  readonly currentWorkspaceId = this.workspaceStore.currentWorkspaceId;
  readonly currentUserId = computed(
    () => this.authStore.user()?.userUID ?? null
  );
  readonly isSwitching = signal(false);
  readonly switchingId = signal<string | null>(null);
  readonly searchTerm = signal('');
  readonly highlightedIndex = signal(0);
  readonly paletteOpen = signal(false);
  readonly sortKey = signal<WorkspaceSortKey>('lastVisit');
  readonly sortDir = signal<SortDirection>('desc');

  readonly workspaces = this.workspaceStore.userWorkspaces;

  readonly displayWorkspaces = computed<DisplayWorkspace[]>(() => {
    const userId = this.currentUserId();
    return this.workspaces().map((ws) => this.toDisplay(ws, userId));
  });

  readonly filtered = computed<DisplayWorkspace[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const list = this.displayWorkspaces();
    if (!term) return list;
    return list.filter(
      (w) =>
        w.name.toLowerCase().includes(term) ||
        w.typeLabel.toLowerCase().includes(term) ||
        w.role.toLowerCase().includes(term)
    );
  });

  readonly sortedRows = computed<DisplayWorkspace[]>(() => {
    const list = [...this.filtered()];
    const key = this.sortKey();
    const dir = this.sortDir() === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      let cmp: number;
      switch (key) {
        case 'name':
          cmp = a.name.localeCompare(b.name, undefined, {
            sensitivity: 'base'
          });
          break;
        case 'properties':
          cmp = a.propertyCount - b.propertyCount;
          break;
        case 'lastVisit':
        default:
          cmp = a.lastActivity - b.lastActivity;
          break;
      }
      return cmp * dir;
    });
    return list;
  });

  readonly totalCount = computed(() => this.workspaces().length);

  private overlayRef: OverlayRef | null = null;

  // ---------- Trigger button helpers ----------

  getCurrentWorkspace(): Workspace | undefined {
    const id = this.currentWorkspaceId();
    return this.workspaces().find((w) => (w.workspaceId || w.id) === id);
  }

  getCurrentWorkspaceName(): string {
    return (
      this.getCurrentWorkspace()?.name ||
      this.translate.instant('shared.workspace.noWorkspace')
    );
  }

  getWorkspaceInitials(workspace: Workspace | undefined): string {
    return resolveWorkspaceInitials(workspace);
  }

  getWorkspaceColor(workspace: Workspace | undefined): string {
    return resolveWorkspaceColor(workspace);
  }

  // ---------- Palette state ----------

  togglePalette(): void {
    if (this.paletteOpen()) {
      this.closePalette();
    } else {
      this.openPalette();
    }
  }

  openPalette(): void {
    if (this.paletteOpen() || !this.paletteTpl) return;
    this.searchTerm.set('');
    this.highlightedIndex.set(this.indexOfCurrent());

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'workspace-palette-backdrop',
      panelClass: 'workspace-palette-panel',
      positionStrategy: this.overlay
        .position()
        .global()
        .centerHorizontally()
        .top('8vh'),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      disposeOnNavigation: true
    });

    const portal = new TemplatePortal(this.paletteTpl, this.vcr);
    this.overlayRef.attach(portal);
    this.overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.closePalette());
    this.paletteOpen.set(true);

    setTimeout(() => this.searchInput?.nativeElement.focus(), 0);

    // Fire-and-forget: refresh stats for any workspace whose `statsComputedAt`
    // is older than the TTL. The Firestore listener pushes the new values back
    // into the store as soon as the callable lands, so the UI updates live
    // (rows mutate from "—" to real numbers without re-opening the palette).
    void this.refreshStaleStats();
  }

  // Skip the recompute when stats were refreshed within this many minutes.
  private static readonly STATS_TTL_MIN = 10;

  private async refreshStaleStats(): Promise<void> {
    const cutoff =
      Date.now() - ToolbarWorkspaceSwitcherComponent.STATS_TTL_MIN * 60_000;
    const stale = this.workspaces().filter((ws) => {
      const ts = this.coerceTimestamp(
        (ws.stats as { statsComputedAt?: unknown } | undefined)?.statsComputedAt
      );
      return !ts || ts < cutoff;
    });
    if (stale.length === 0) return;

    await Promise.all(
      stale.map(async (ws) => {
        const id = ws.workspaceId || ws.id;
        try {
          await new Promise<void>((resolve, reject) => {
            this.workspaceService
              .updateWorkspaceStats(id)
              .pipe(take(1))
              .subscribe({
                next: () => resolve(),
                error: (err) => reject(err)
              });
          });
        } catch (err) {
          // Non-fatal: a missing function or permission error just leaves the
          // metric cells as "—". Other workspaces still get their refresh.
          console.debug(
            '[workspace-switcher] stats refresh failed for',
            id,
            err
          );
        }
      })
    );
  }

  closePalette(): void {
    if (!this.paletteOpen()) return;
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.paletteOpen.set(false);
  }

  trackById(_i: number, item: DisplayWorkspace): string {
    return item.id;
  }

  // ---------- Sorting ----------

  toggleSort(key: WorkspaceSortKey): void {
    if (this.sortKey() === key) {
      this.sortDir.set(this.sortDir() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortKey.set(key);
      this.sortDir.set(key === 'name' ? 'asc' : 'desc');
    }
    this.highlightedIndex.set(0);
  }

  // ---------- Selection / activation ----------

  onItemHover(index: number): void {
    this.highlightedIndex.set(index);
  }

  isHighlighted(item: DisplayWorkspace): boolean {
    const flat = this.sortedRows();
    return flat[this.highlightedIndex()]?.id === item.id;
  }

  isActive(item: DisplayWorkspace): boolean {
    return item.id === this.currentWorkspaceId();
  }

  async activate(item: DisplayWorkspace): Promise<void> {
    if (this.isSwitching() || item.id === this.currentWorkspaceId()) {
      this.closePalette();
      return;
    }
    this.isSwitching.set(true);
    this.switchingId.set(item.id);
    try {
      const fromWs = this.getCurrentWorkspace();
      try {
        // sortedRows() is the exact order the user had on screen at click
        // time (search + sort already applied) — the curtain's rail scrolls
        // through that same order, not a recomputed one.
        const orderedRows: WorkspaceSwitchRailEntry[] = this.sortedRows().map(
          (row) => ({
            id: row.id,
            name: row.name,
            initials: row.initials,
            color: row.color
          })
        );
        const marker: WorkspaceSwitchMarker = {
          toId: item.id,
          toName: item.name,
          fromName: fromWs?.name ?? '',
          rail: buildWorkspaceSwitchRail(orderedRows, item.id)
        };
        sessionStorage.setItem(
          'proporio_workspace_switch',
          JSON.stringify(marker)
        );
      } catch {
        // sessionStorage unavailable — degrade gracefully.
      }
      await this.workspaceStore.switchWorkspace(item.id);
      window.location.href = '/';
    } catch (error) {
      try {
        sessionStorage.removeItem('proporio_workspace_switch');
      } catch {
        /* ignore */
      }
      console.error('Error switching workspace:', error);
      // A rejected switchWorkspace() used to leave the user staring at the
      // unchanged toolbar with no explanation (DESIGN.md §13) — exactly what
      // "j'ouvre un espace et je retombe sur l'original" describes (#465).
      this.uiFeedback.toast.error(
        this.translate.instant('shared.workspace.switchError')
      );
      this.isSwitching.set(false);
      this.switchingId.set(null);
    }
  }

  openInNewTab(item: DisplayWorkspace): void {
    if (item.id === this.currentWorkspaceId()) {
      window.open(window.location.origin + '/', '_blank');
      return;
    }
    try {
      localStorage.setItem('proporio_pending_workspace_switch', item.id);
    } catch {
      // localStorage unavailable — new tab will land on the default workspace.
    }
    window.open(window.location.origin + '/', '_blank');
    this.closePalette();
  }

  async openCreateWorkspaceDialog(): Promise<void> {
    this.closePalette();
    try {
      const { CreateWorkspaceDialogComponent } =
        await import('../../../../pages/apps/workspace/create-workspace-dialog/create-workspace-dialog.component');
      this.dialog.open(CreateWorkspaceDialogComponent, {
        width: '680px',
        maxWidth: '92vw',
        panelClass: 'create-workspace-dialog-panel',
        backdropClass: 'workspace-palette-backdrop'
      });
    } catch (error) {
      console.error('Error opening create workspace dialog:', error);
    }
  }

  // ---------- Keyboard ----------

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(ev: KeyboardEvent): void {
    const isCmdK = (ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === 'k';
    if (isCmdK) {
      ev.preventDefault();
      this.togglePalette();
      return;
    }
    if (!this.paletteOpen()) return;

    switch (ev.key) {
      case 'Escape':
        ev.preventDefault();
        this.closePalette();
        return;
      case 'ArrowDown':
        ev.preventDefault();
        this.moveHighlight(1);
        return;
      case 'ArrowUp':
        ev.preventDefault();
        this.moveHighlight(-1);
        return;
      case 'Enter': {
        ev.preventDefault();
        const item = this.sortedRows()[this.highlightedIndex()];
        if (!item) return;
        if (ev.metaKey || ev.ctrlKey) {
          this.openInNewTab(item);
        } else {
          void this.activate(item);
        }
        return;
      }
    }
  }

  private moveHighlight(delta: number): void {
    const items = this.sortedRows();
    if (items.length === 0) return;
    const next =
      (this.highlightedIndex() + delta + items.length) % items.length;
    this.highlightedIndex.set(next);
    queueMicrotask(() => this.scrollHighlightedIntoView());
  }

  private scrollHighlightedIntoView(): void {
    if (!this.overlayRef) return;
    const root = this.overlayRef.overlayElement;
    const el = root?.querySelector<HTMLElement>('.power-row--highlighted');
    el?.scrollIntoView({ block: 'nearest' });
  }

  private indexOfCurrent(): number {
    const flat = this.sortedRows();
    const id = this.currentWorkspaceId();
    const idx = flat.findIndex((w) => w.id === id);
    return idx >= 0 ? idx : 0;
  }

  // ---------- Display helpers ----------

  formatRelativeTime(ms: number): string {
    if (!ms) return this.translate.instant('shared.workspace.never');
    const diff = Date.now() - ms;
    if (diff < 0 || diff < 60_000)
      return this.translate.instant('shared.workspace.now');
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 60)
      return this.translate.instant('shared.workspace.minutesAgo', {
        count: minutes
      });
    const hours = Math.floor(minutes / 60);
    if (hours < 24)
      return this.translate.instant('shared.workspace.hoursAgo', {
        count: hours
      });
    const days = Math.floor(hours / 24);
    if (days < 7)
      return this.translate.instant('shared.workspace.daysAgo', {
        count: days
      });
    const weeks = Math.floor(days / 7);
    if (weeks < 5)
      return this.translate.instant('shared.workspace.weeksAgo', {
        count: weeks
      });
    const months = Math.floor(days / 30);
    return this.translate.instant('shared.workspace.monthsAgo', {
      count: months
    });
  }

  formatMoneyShort(amountEUR: number | null): string {
    if (amountEUR === null || amountEUR === undefined) return '';
    const abs = Math.abs(amountEUR);
    if (abs >= 1_000_000) return `${(amountEUR / 1_000_000).toFixed(1)} M €`;
    if (abs >= 1_000) return `${Math.round(amountEUR / 1_000)} k €`;
    return `${Math.round(amountEUR)} €`;
  }

  // ---------- Build a DisplayWorkspace ----------

  private toDisplay(ws: Workspace, userId: string | null): DisplayWorkspace {
    const id = ws.workspaceId || ws.id;
    const stats = ws.stats as
      | {
          propertyCount?: number;
          unitsCount?: number;
          monthlyRentEUR?: number;
          cashOverdueEUR?: number;
          cashIsOnTrack?: boolean;
          inboxUrgentCount?: number;
          inboxTotalCount?: number;
          lastActivityAt?: unknown;
        }
      | undefined;
    const lastActivity =
      this.coerceTimestamp(stats?.lastActivityAt) ??
      this.coerceTimestamp(ws.updatedAt) ??
      0;
    const role = this.resolveRole(ws, userId);

    return {
      raw: ws,
      id,
      name: ws.name,
      initials: this.getWorkspaceInitials(ws),
      color: this.getWorkspaceColor(ws),
      role: this.translate.instant(
        ROLE_LABEL_KEYS[role] ?? 'shared.workspace.roles.member'
      ),
      typeLabel: this.translate.instant(
        TYPE_LABEL_KEYS[ws.type ?? 'personal'] ?? TYPE_LABEL_KEYS['personal']
      ),
      propertyCount: stats?.propertyCount ?? 0,
      unitsCount:
        typeof stats?.unitsCount === 'number' ? stats.unitsCount : null,
      lastActivity,
      warningCount: stats?.inboxUrgentCount ?? 0,
      monthlyRentEUR:
        typeof stats?.monthlyRentEUR === 'number' ? stats.monthlyRentEUR : null,
      cashOverdueEUR:
        typeof stats?.cashOverdueEUR === 'number' ? stats.cashOverdueEUR : null,
      cashIsOnTrack:
        typeof stats?.cashIsOnTrack === 'boolean' ? stats.cashIsOnTrack : null,
      inboxUrgentCount: stats?.inboxUrgentCount ?? 0,
      inboxTotalCount: stats?.inboxTotalCount ?? 0,
      isShared: !!userId && ws.ownerId !== userId
    };
  }

  private resolveRole(ws: Workspace, userId: string | null): string {
    if (!userId) return 'standard';
    if (ws.ownerId === userId) return 'owner';
    return ws.members?.[userId]?.role ?? 'standard';
  }

  private coerceTimestamp(value: unknown): number | null {
    if (!value) return null;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const t = Date.parse(value);
      return isNaN(t) ? null : t;
    }
    if (typeof value === 'object') {
      const v = value as { seconds?: number; toMillis?: () => number };
      if (typeof v.toMillis === 'function') return v.toMillis();
      if (typeof v.seconds === 'number') return v.seconds * 1000;
    }
    return null;
  }
}
